import { RolesFromApi } from "@/app/config/constants";
import {
  DELETE,
  GET,
  GET_FILE,
  PATCH,
  POST,
  PUT,
} from "@/app/services/apiService";
import { createSlice } from "@reduxjs/toolkit";

export async function getPatientProfile(patientId: number) {
  const profileConfig = { url: "patients/" + patientId };
  const response = await GET(profileConfig);
  return response;
}

export const getInsulinPrescription = async (patientId: number) => {
  const config = {
    url: `insulin-intakes/patients/${patientId}/prescription`,
  };
  const response = await GET(config);
  return response;
};

export const getInsulinList = async (patientId = 0) => {
  const config = {
    url: `insulin-rbs/insulins`,
    params: { patientId },
  };

  const response = await GET(config);
  return response;
};

export const updatePrescription = async (prescription: any) => {
  const config = {
    url: `insulin-rbs/prescription`,
    data: prescription,
  };

  const response = await POST(config);
  return response;
};

export const deletePrescription = async (prescription: any) => {
  const config = {
    url: `insulin-rbs/prescription`,
    data: prescription,
  };

  const response = await POST(config);
  return response;
};

export const getGlucoseHistory = async (param: any) => {
  const config = {
    url: "glucose",
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getAllLabTest = async () => {
  const labTestsConfig = { url: `/lab-tests` };
  const response = await GET(labTestsConfig);
  return response;
};

export const getAverageFBS = async (patientId: number) => {
  const config = {
    url: "glucose/summary",
    params: { patientId },
  };
  const response = await GET(config);
  return response;
};

export const getWeightHistory = async (patientId: number, param: any) => {
  const config = {
    url: `patients/${patientId}/weight`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getClinicalNotes = async (patientId: number, param: any) => {
  const config = {
    url: `patients/${patientId}/clinical-notes`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getVitalList = async (patientId: number, param: any) => {
  const config = {
    url: `patients/${patientId}/vitals`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getLabTestReportList = async (patientId: number, param: any) => {
  const config = {
    url: `patients/${patientId}/lab-reports`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getAbnormalityReportList = async (
  patientId: number,
  param: any
) => {
  const config = {
    url: `patients/${patientId}/abnormalities`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getActivityJournals = async (patientId: number, param: any) => {
  const config = {
    url: `patients/${patientId}/activityJournal`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getHealthVaultSummary = async (patientId: number) => {
  const config = {
    url: `patients/${patientId}/healthVault`,
  };
  const response = await GET(config);
  return response;
};

export const saveClinicalNote = async (
  patientId: number,
  param: any,
  noteId: number | null = null
) => {
  const config = {
    url: `patients/${patientId}/clinical-notes` + (noteId ? "/" + noteId : ""),
    data: { ...param },
  };
  const response = (await noteId) ? PUT(config) : POST(config);
  return response;
};

export const getClinicalDocuments = async (patientId: number, param: any) => {
  const config = {
    url: `patients/${patientId}/clinical-documents`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getAllDietPlans = async (param: any) => {
  const config = {
    url: `diets`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const linkPatientWithDietPlan = async (param: any) => {
  const config = {
    url: "diets/assign",
    data: { ...param },
  };
  const response = await POST(config);
  return response;
};

export const UnlinkPatientWithDietPlan = async (
  patientId: number,
  dietId: number
) => {
  const config = {
    url: `diets/${dietId}/patients/${patientId}`,
  };

  const response = await DELETE(config);
  return response;
};

export const getAttachmentPath = async (
  url: string,
  key: string,
  contentType: string,
  isDownloading: boolean = false
) => {
  const header: any = { key };
  if (!isDownloading) header["x-response-content-type"] = contentType;
  const req = {
    url: url,
    headers: header,
  };
  const res: any = await GET(req);
  return res;
};

export const getVitalSummary = async (patientId: number) => {
  const config = {
    url: `patients/${patientId}/vitals/summary`,
  };
  const response = await GET(config);
  return response;
};
export const getVitalHistory = async (
  patientId: number,
  lookupKey: string,
  param: any
) => {
  const config = {
    url: `patients/${patientId}/vitals/${lookupKey}`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getLabReportSummary = async (patientId: number) => {
  const config = {
    url: `patients/${patientId}/lab-reports/summary`,
  };
  const response = await GET(config);
  return response;
};

export const getLabReportHistory = async (
  patientId: number,
  lookupKey: string,
  param: any
) => {
  const config = {
    url: `patients/${patientId}/lab-reports/tests/${lookupKey}`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getPatientUpdatesHistory = async (param: any) => {
  const config = {
    url: `clinical/notification`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getPatientSummaryList = async (param: any) => {
  const config = {
    url: `patient/summary`,
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getDoctorList = async (param: any, role: string | null = null) => {
  let config: any = {
    url: "doctors",
    params: { ...param },
  };
  if (role) {
    config = { ...config, headers: { "x-user-role": role } };
  }
  const response = await GET(config);
  return response;
};

export const exportDoctorData = async (param: any, role: string | null) => {
  if (param.limit) {
    delete param.limit;
    delete param.offset;
  }
  let getConfig: any = {
    responseType: "arraybuffer",
    params: { ...param },
    url: `/doctors/export`,
  };
  if (role) {
    getConfig = { ...getConfig, headers: { "x-user-role": role } };
  }
  const res = await GET_FILE(getConfig);
  return res;
};

export const getDoctorProfile = async (docId: number) => {
  const config = {
    url: "doctors/" + docId,
  };
  const response = await GET(config);
  return response;
};

export const getDoctorLinkedPatients = async (docId: number, reqParam: any) => {
  const config: any = {
    url: "doctors/" + docId + "/patients",
    params: reqParam,
  };

  const response = await GET(config);
  return response;
};

export const updateDoctorProfile = async (
  coachId: number | null = null,
  data: any = {}
) => {
  const config = {
    url: "doctors/" + (coachId || ""),
    data: data,
  };
  const response = (await coachId) ? PUT(config) : POST(config);
  return response;
};

export const addMoreClinics = async (
  doctorId: string | null = null,
  data: any = {}
) => {
  const config = {
    url: "doctors/" + doctorId + "/clinic",
    data: data,
  };
  const response = await POST(config);
  return response;
};

export const moveClinic = async (
  doctorId: number | null = null,
  data: any = {}
) => {
  const config = {
    url: "doctors/" + doctorId + "/clinic/" + data.clinicUid,
    data: data,
  };
  const response = await DELETE(config);
  return response;
};

export const generateDoctorInviteCode = async (data: any = {}) => {
  if (data.prefix && data.prefix.length === 0) delete data.prefix;
  const config = {
    url: "invite/doctor",
    data: data,
  };
  const response = POST(config);
  return response;
};

export const getCoachList = async (param: any) => {
  const config = {
    url: "coaches",
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getCoachProfile = async (coachId: number) => {
  const config = {
    url: "coaches/" + coachId,
  };
  const response = await GET(config);
  return response;
};

export const updateCoachProfile = async (
  coachId: number | null = null,
  data: any = {}
) => {
  const config = {
    url: "coaches/" + (coachId || ""),
    data: data,
  };
  const response = (await coachId) ? PUT(config) : POST(config);
  return response;
};

export const getClinicList = async (param: any) => {
  const config = {
    url: "clinics",
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getLinkUserList = async (
  url: string,
  param: any,
  role: string | null = null
) => {
  let config: any = {
    url: url,
    params: { ...param },
  };
  if (role) {
    config = { ...config, headers: { "x-user-role": role } };
  }
  const response = await GET(config);
  return response;
};

export const setLinkWithUser = async (
  url: string,
  patientId: any,
  userId: any
) => {
  const config = {
    url: "patients/" + patientId + "/" + url + "/" + userId,
  };
  const response = await PUT(config);
  return response;
};

export const markAsPremium = async (patientId: number, params: any) => {
  const config = {
    url: "patients/" + patientId + "/special",
    data: params,
  };
  const response = await PATCH(config);
  return response;
};

export const getClinicProfile = async (clinicId: number) => {
  const config = {
    url: "clinics/" + clinicId,
  };
  const response = await GET(config);
  return response;
};

export const updateClinicProfile = async (
  coachId: number | null = null,
  data: any = {}
) => {
  const config = {
    url: "clinics/" + (coachId || ""),
    data: data,
  };

  const response = coachId ? await PUT(config) : await POST(config);
  return response;
};

export const getAdminList = async (param: any) => {
  const config = {
    url: "customer-support",
    params: { ...param },
  };
  const response = await GET(config);
  return response;
};

export const getAdminProfile = async (adminId: number) => {
  const config = {
    url: "customer-support/" + adminId,
  };
  const response = await GET(config);
  return response;
};

export const updateAdminProfile = async (
  adminId: number | null = null,
  data: any = {}
) => {
  const config = {
    url: "customer-support/" + (adminId || ""),
    data: data,
  };
  const response = await (adminId ? PUT(config) : POST(config));
  return response;
};

export const getPatientList = async (
  param: any,
  isClinicAdmin: boolean = false
) => {
  let config: any = {
    url: "patients",
    params: { ...param },
  };
  if (isClinicAdmin) {
    config = {
      ...config,
      headers: { "x-user-role": RolesFromApi.clinicAdmin },
    };
  }
  const response = await GET(config);
  return response;
};

export const getLinkRequestList = async (param: any) => {
  let config: any = {
    url: "link-request",
    params: { ...param },
  };

  const response = await GET(config);
  return response;
};

// export const exportPatientList = async (param: any) => {
//   let config: any = {
//     url: "patients/export",
//     params: { ...param },
//   };

//   const response = await GET(config);
//   return response;
// };

export const linkRequestAction = async (data: any = {}) => {
  let config: any = {
    url: "link-request/" + data.requestId + "/" + data.action,
  };

  const response = await PUT(config);
  return response;
};

export const getPatientDashboardStatistics = async (
  param: any,
  options: any = {}
) => {
  // Ensure groupBy is sent properly as an array without corruptions
  const normalized: any = { ...param };
  if (param && param.groupBy && Array.isArray(param.groupBy)) {
    normalized.groupBy = param.groupBy;
  }
  const config: any = {
    url: "dashboard/user-stats",
    params: normalized,
  };
  if (options && options.signal) config.signal = options.signal;
  const response = await GET(config);
  return response;
};

export const getFeatureMatrix = async (param: any, options: any = {}) => {
  const config: any = {
    url: "dashboard/feature-matrix",
    params: { ...param },
  };
  if (options && options.signal) config.signal = options.signal;
  const response = await GET(config);
  return response;
};

export const getMonthlyActivityDoctorPatientList = async (
  param: any,
  options: any = {}
) => {
  const config: any = {
    url: "dashboard/doctor-patient-stats",
    params: { ...param },
  };
  if (options && options.signal) config.signal = options.signal;
  const response = await GET(config);
  return response;
};

export const exportPatientData = async (param: any) => {
  const config = {
    responseType: "arraybuffer",
    url: "patients/export",
    params: { ...param },
  };
  const response = await GET_FILE(config);
  return response;
};

export async function deleteUserAccount(reqBody: any) {
  const userConfig = { url: `accounts`, data: reqBody };
  const response = await DELETE(userConfig);
  return response;
}

export const updatePatientReminder = async (
  reminderId: number | null = null,
  data: any = {}
) => {
  const config = {
    url: "followups/" + (reminderId || ""),
    data: data,
  };
  const response = (await reminderId) ? PUT(config) : POST(config);
  return response;
};

export const referADoctor = async (data: any = {}) => {
  const config = {
    url: "doctors/assign-doctor",
    data: data,
  };
  const response = POST(config);
  return response;
};

export const removeReferredDoctor = async (
  doctorId: number,
  patientId: number
) => {
  const config = {
    url: "doctors/assign-doctor/" + doctorId,
    data: { patientId: patientId },
  };
  const response = DELETE(config);
  return response;
};

export const deletePatientReminder = async (reminderId: number) => {
  const config = {
    url: "followups/" + reminderId,
  };
  const response = await DELETE(config);
  return response;
};

export const getPatientDetailsById = async (uid: string) => {
  const config = {
    url: "users/" + uid + "/check",
  };
  const response = await GET(config);
  return response;
};

export const linkPatientByDoctor = async (docUID: string, patUID: string) => {
  const config = {
    url: "link-request",
    data: {
      patientUid: patUID,
      doctorUid: docUID,
    },
  };
  const response = await POST(config);
  return response;
};

const initialState: any = {
  activeDoctor: null,
  listViewUpdates: null,
  linkRequestCount: { sent: 0, received: 0 },
  userFilter: {
    doctorList: null,
    coachList: null,
    clinicList: null,
    patientList: null,
    adminList: null,
    doctor_patientList: null,
    coach_patientList: null,
  },
};

export const doctorReducer = createSlice({
  name: "doctor",
  initialState,

  reducers: {
    saveAuthTokens: (state, action) => {
      state.list.push(action.payload);
    },

    saveAuthUser: (state, action) => {
      state.userProfile = action.payload;
    },
    removeAuthUser: (state) => {
      state.userProfile = null;
    },
    saveLabTest: (state, action) => {
      state.availableLabTest = action.payload;
    },
    saveHealthVaultSummary: (state, action) => {
      state.healthVaultSummary = action.payload;
    },
    saveActiveDoctor: (state, action) => {
      state.activeDoctor = action.payload;
    },
    updateListView: (state, action) => {
      state.listViewUpdates = action.payload;
    },
    saveUserFilter: (state, action) => {
      state.userFilter = { ...state.userFilter, ...action.payload };
    },
    clearUserFilter: (state) => {
      state.userFilter = { ...initialState.userFilter };
    },
    saveLinkRequestCount: (state, action) => {
      state.linkRequestCount = {
        sent: action.payload.sent,
        received: action.payload.received,
      };
    },
  },
});

export const {
  updateListView,
  saveAuthUser,
  saveLinkRequestCount,
  saveUserFilter,
  clearUserFilter,
  removeAuthUser,
  saveLabTest,
  saveHealthVaultSummary,
  saveActiveDoctor,
} = doctorReducer.actions;
export default doctorReducer.reducer;
